import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { EventRow } from "./types";

interface Options {
  app: string | null;
  scope?: "upcoming" | "mine" | "all";
}

/** List events for the current app + an optional "scope" filter. */
export const useEvents = ({ app, scope = "upcoming" }: Options) => {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!app) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    let q = supabase.from("events").select("*").eq("app", app as any);

    if (scope === "upcoming") {
      q = q
        .eq("status", "published")
        .gte("starts_at", new Date(Date.now() - 60 * 60 * 1000).toISOString())
        .order("starts_at", { ascending: true });
    } else if (scope === "mine") {
      if (!user) {
        setEvents([]);
        setLoading(false);
        return;
      }
      q = q.eq("host_user_id", user.id).order("starts_at", { ascending: false });
    } else {
      q = q.order("starts_at", { ascending: false });
    }

    const { data, error } = await q.limit(60);
    if (error) setError(error.message);
    setEvents((data as EventRow[]) ?? []);
    setLoading(false);
  }, [app, scope, user]);

  useEffect(() => {
    void load();
  }, [load]);

  const createEvent = useCallback(
    async (
      patch: Omit<
        EventRow,
        "id" | "host_user_id" | "created_at" | "updated_at" | "status"
      > & { status?: string },
    ) => {
      if (!user) return { error: "Not signed in" };
      const row = {
        ...patch,
        host_user_id: user.id,
        status: patch.status ?? "published",
      };
      const { data, error } = await supabase
        .from("events")
        .insert(row as any)
        .select("*")
        .maybeSingle();
      if (error) return { error: error.message };
      await load();
      return { data: data as EventRow };
    },
    [user, load],
  );

  return { events, loading, error, reload: load, createEvent };
};
